import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./logincss/Login.css";
import logo from "../../images/auravit_logo.png";
import { auth } from "../../firebase";
import { userService } from "../../services/firestoreService";
import { sendOtpEmail, sendOtpSms } from "../../services/otpService";
import {
  createPasswordResetOtpChallenge,
} from "../../services/authFlowService";

function ForgotPassword() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("email"); // 'email' or 'phone'
  const [email, setEmail] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Email validation regex
  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  // Contact number validation
  const validateContactNumber = (value) => {
    if (!value) return false;
    if (!/^\d+$/.test(value)) return false; // numeric only
    if (value.length !== 10) return false; // exactly 10 digits
    if (!value.startsWith('9')) return false; // must start with 9
    return true;
  };

  const handleEmailChange = (e) => {
    let value = e.target.value;

    // Block input beyond 254 characters
    if (value.length > 254) return;

    setEmail(value);

    // Live validation while typing
    if (!value) {
      setError("Email is required");
    } else if (!validateEmail(value)) {
      setError("Invalid email format");
    } else {
      setError("");
    }
  };

  const handleContactNumberChange = (e) => {
    let value = e.target.value;

    // Only allow numeric input
    if (!/^\d*$/.test(value)) return;

    // Limit to 10 digits
    if (value.length > 10) return;

    setContactNumber(value);

    // Live validation while typing
    if (!value) {
      setError("");
    } else if (!/^\d+$/.test(value)) {
      setError("Contact number must be numeric only");
    } else if (value.length !== 10) {
      setError("Contact number must be 10 digits");
    } else if (!value.startsWith('9')) {
      setError("Contact number must start with 9 (e.g., 9123456789)");
    } else {
      setError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return; // Prevent duplicate submissions
    setIsSubmitting(true);

    if (mode === "email") {
      if (!email) {
        setError("Email is required");
        setIsSubmitting(false);
        return;
      }
      if (!validateEmail(email)) {
        setError("Invalid email format.");
        setIsSubmitting(false);
        return;
      }

      const cleanedEmail = email.toLowerCase().trim();

      try {
        const account = await userService.getUserByEmail(cleanedEmail);
        
        if (!account) {
          setError("No Account Found");
          return;
        }
        
        // For staff and admin accounts, skip strict verification check
        // For patient accounts, require verified: true
        const isStaffOrAdmin = account.accountType === 'Staff' || account.accountType === 'Admin';
        const isVerified = isStaffOrAdmin || account.verified === true;
        
        if (!isVerified) {
          setError("No Account Found");
          return;
        }

        const challenge = createPasswordResetOtpChallenge(cleanedEmail);
        await sendOtpEmail({
          email: cleanedEmail,
          otp: challenge.otp,
          purpose: "forgot-password",
          requestId: challenge.requestId,
          expiresAt: challenge.expiresAt,
        });
        navigate("/password-verification", {
          state: { email: cleanedEmail, type: "forgot-password" },
        });
      } catch (err) {
        console.error("Password reset error:", err);
        if (err.code === "auth/user-not-found") {
          setError("No account found with this email.");
        } else {
          setError(err.message || "Failed to send reset OTP email.");
        }
      } finally {
        setIsSubmitting(false);
      }
    } else {
      if (!contactNumber) {
        setError("Contact number is required");
        setIsSubmitting(false);
        return;
      }
      if (!validateContactNumber(contactNumber)) {
        setError("Contact number must be 10 digits starting with 9 (e.g., 9123456789)");
        setIsSubmitting(false);
        return;
      }

      const formattedPhone = `+63${contactNumber}`;

      try {
        // Check if phone exists using the existing checkPhoneExists function
        const phoneExists = await userService.checkPhoneExists(contactNumber);
        
        if (!phoneExists) {
          setError("No Account Found");
          return;
        }

        const challenge = createPasswordResetOtpChallenge(formattedPhone);
        await sendOtpSms({
          phone: formattedPhone,
          otp: challenge.otp,
          purpose: "forgot-password",
          requestId: challenge.requestId,
          expiresAt: challenge.expiresAt,
        });
        navigate("/password-verification", {
          state: { email: formattedPhone, type: "forgot-password", isPhone: true },
        });
      } catch (err) {
        console.error("Password reset error:", err);
        setError(err.message || "Failed to send reset OTP SMS.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Determine if Send button is enabled
  const isInputValid = mode === "email" ? validateEmail(email) : validateContactNumber(contactNumber);

  const handleModeToggle = () => {
    setMode(mode === "email" ? "phone" : "email");
    setError("");
    setEmail("");
    setContactNumber("");
  };

  return (
    <>
    <div className="login-logo-container">
      <img
        src={logo}
        alt="AuraVit"
        className="login-logo"
        onClick={() => navigate("/")}
      />
    </div>
    <div className="forgot-container">
      <div className="forgot-card">
        <h1 className="forgot-title">Forgot Password?</h1>
        <p className="forgot-subtitle">
          {mode === "email" 
            ? "Enter your email to receive a 6-digit verification code."
            : "Enter your contact number to receive a 6-digit verification code."}
        </p>

        <form className="forgot-form" onSubmit={handleSubmit}>
          {mode === "email" ? (
            <div className="input-group">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={handleEmailChange}
                className={error ? "input-error" : ""}
              />
              {error && <div className="field-error">{error}</div>}
            </div>
          ) : (
            <div className="input-group">
              <div className={`phone-input-group ${error ? "phone-input-group-error" : ""}`}>
                <span className="phone-country-code">+63</span>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="9123456789"
                  value={contactNumber}
                  onChange={handleContactNumberChange}
                  className={error ? "input-error" : ""}
                  maxLength={10}
                />
              </div>
              {error && <div className="field-error">{error}</div>}
            </div>
          )}

          <button type="submit" className="forgot-btn" disabled={!isInputValid || isSubmitting}>
            Send
          </button>
        </form>

        <div className="forgot-actions-row">
          <span className="back-link" onClick={() => navigate("/login")}>
            &lt; Back to Sign In
          </span>
          <span className="back-link" onClick={handleModeToggle}>
            Try Another Way
          </span>
        </div>
      </div>
    </div>
    </>
  );
}

export default ForgotPassword;
